import { useState, useMemo, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  Cell,
} from 'recharts';
import { TicketStatus, Priority } from '../../types/ticket';
import type { Ticket, Tech } from '../../types/ticket';
import { useTicketRepository, useTechRepository } from '../../data/hooks';
import { AnalyticsExplorer } from './AnalyticsExplorer';
import './StatsView.css';

// KPI Card Component
interface KPICardProps {
  label: string;
  value: string | number;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
    label: string;
  };
  icon: React.ReactNode;
  accentColor?: string;
}

const KPICard: React.FC<KPICardProps> = ({ label, value, trend, icon, accentColor = '#1976d2' }) => {
  const getTrendIcon = () => {
    if (!trend) return null;
    if (trend.direction === 'up') {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 4L12 8H9V12H7V8H4L8 4Z" fill="currentColor" />
        </svg>
      );
    }
    if (trend.direction === 'down') {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 12L4 8H7V4H9V8H12L8 12Z" fill="currentColor" />
        </svg>
      );
    }
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 8H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  };

  const getTrendColor = () => {
    if (!trend) return '';
    if (trend.direction === 'up') return 'stats-kpi__trend--up';
    if (trend.direction === 'down') return 'stats-kpi__trend--down';
    return 'stats-kpi__trend--neutral';
  };

  return (
    <div className="stats-kpi" style={{ '--accent-color': accentColor } as React.CSSProperties}>
      <div className="stats-kpi__header">
        <div className="stats-kpi__icon">{icon}</div>
        <span className="stats-kpi__label">{label}</span>
      </div>
      <div className="stats-kpi__value">{value}</div>
      {trend && (
        <div className={`stats-kpi__trend ${getTrendColor()}`}>
          {getTrendIcon()}
          <span className="stats-kpi__trend-value">{Math.abs(trend.value)}%</span>
          <span className="stats-kpi__trend-label">{trend.label}</span>
        </div>
      )}
    </div>
  );
};

// Chart Card Component
interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, subtitle, children }) => (
  <div className="stats-chart-card">
    <div className="stats-chart-card__header">
      <h3 className="stats-chart-card__title">{title}</h3>
      {subtitle && <p className="stats-chart-card__subtitle">{subtitle}</p>}
    </div>
    <div className="stats-chart-card__content">{children}</div>
  </div>
);

// Priority colors matching the app's design system
const PRIORITY_COLORS: Record<string, string> = {
  P1: '#d13438',
  P2: '#ca5010',
  P3: '#8a8886',
  P4: '#107c10',
};

// Tech colors for charts - unique color for each tech (10 total)
const TECH_COLORS = [
  '#1976d2', // Blue
  '#107c10', // Green
  '#7b1fa2', // Purple
  '#c2185b', // Pink
  '#f57c00', // Orange
  '#00838f', // Teal
  '#5c6bc0', // Indigo
  '#8d6e63', // Brown
  '#43a047', // Light Green
  '#e91e63', // Magenta
];

type StatsTabType = 'overview' | 'analytics';

export const StatsView: React.FC = () => {
  const ticketRepo = useTicketRepository();
  const techRepo = useTechRepository();

  const [activeTab, setActiveTab] = useState<StatsTabType>('overview');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [techs, setTechs] = useState<Tech[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load data from repositories
  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        const [loadedTickets, loadedTechs] = await Promise.all([
          ticketRepo.getAll(),
          techRepo.getAll(),
        ]);
        if (mounted) {
          setTickets(loadedTickets);
          setTechs(loadedTechs);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to load stats data:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, [ticketRepo, techRepo]);

  // Calculate KPI data
  const kpiData = useMemo(() => {
    const openStatuses: TicketStatus[] = [
      TicketStatus.NEW,
      TicketStatus.IN_PROGRESS,
      TicketStatus.WAITING,
    ];
    const openJobs = tickets.filter((t) => openStatuses.includes(t.status));

    // Simulate completed today (for demo - would be calculated from actual resolved tickets)
    const completedToday = 23;

    // Calculate average resolution time (simulated for demo)
    const avgResolutionMinutes = 34;
    const avgResolutionTime = `${avgResolutionMinutes}m`;

    return {
      totalOpen: openJobs.length,
      completedToday,
      avgResolutionTime,
    };
  }, [tickets]);

  // Open jobs per tech data
  const openJobsPerTech = useMemo(() => {
    const openStatuses: TicketStatus[] = [
      TicketStatus.NEW,
      TicketStatus.IN_PROGRESS,
      TicketStatus.WAITING,
    ];

    return techs.map((tech) => ({
      name: tech.name.split(' ')[0], // First name only for chart
      fullName: tech.name,
      openJobs: tickets.filter(
        (t) => t.assignedTechId === tech.id && openStatuses.includes(t.status)
      ).length,
      capacity: tech.maxLoad,
    }));
  }, [tickets, techs]);

  // Completed jobs per tech for the week (simulated data)
  const completedJobsPerTech = useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

    return days.map((day, dayIndex) => {
      const dayData: Record<string, string | number> = { day };
      techs.forEach((tech) => {
        // Simulate completed jobs per day (random but consistent)
        const seed = tech.id.charCodeAt(tech.id.length - 1) + dayIndex;
        dayData[tech.name.split(' ')[0]] = Math.floor((seed * 7) % 6) + 1;
      });
      return dayData;
    });
  }, [techs]);

  // Jobs by priority
  const jobsByPriority = useMemo(() => {
    const priorities = [Priority.P1, Priority.P2, Priority.P3, Priority.P4];
    return priorities.map((priority) => ({
      priority,
      count: tickets.filter((t) => t.priority === priority).length,
      color: PRIORITY_COLORS[priority],
    }));
  }, [tickets]);

  // Jobs over time (simulated last 7 days)
  const jobsOverTime = useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map((day, index) => ({
      day,
      created: Math.floor(Math.random() * 8) + 5 + index,
      resolved: Math.floor(Math.random() * 7) + 4 + index,
    }));
  }, []);

  if (isLoading) {
    return null;
  }

  return (
    <div className="stats-view">
      {/* Sub-tabs */}
      <div className="stats-view__tabs">
        <button
          className={`stats-view__tab ${activeTab === 'overview' ? 'stats-view__tab--active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 2h5v5H2V2zm0 7h5v5H2V9zm7-7h5v5H9V2zm0 7h5v5H9V9z" opacity="0.9" />
          </svg>
          <span>Overview</span>
        </button>
        <button
          className={`stats-view__tab ${activeTab === 'analytics' ? 'stats-view__tab--active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 12L6 6L10 9L14 3" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="14" cy="3" r="1.5" fill="currentColor" stroke="none" />
          </svg>
          <span>Analytics Explorer</span>
        </button>
        <div className="stats-view__tab-indicator" data-tab={activeTab} />
      </div>

      {activeTab === 'overview' ? (
        <div className="stats-view__content">
          {/* KPI Cards */}
          <div className="stats-view__kpi-row">
        <KPICard
          label="Total Open Jobs"
          value={kpiData.totalOpen}
          trend={{ value: 12, direction: 'down', label: 'vs last week' }}
          accentColor="#1976d2"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M9 12h6M12 9v6" />
            </svg>
          }
        />
        <KPICard
          label="Jobs Completed Today"
          value={kpiData.completedToday}
          trend={{ value: 23, direction: 'up', label: 'vs yesterday' }}
          accentColor="#107c10"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          }
        />
        <KPICard
          label="Avg Resolution Time"
          value={kpiData.avgResolutionTime}
          trend={{ value: 8, direction: 'down', label: 'vs last week' }}
          accentColor="#0078d4"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 3" />
            </svg>
          }
        />
      </div>

      {/* Charts Row 1 */}
      <div className="stats-view__charts-row">
        <ChartCard title="Open Jobs by Tech" subtitle="Current workload distribution">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={openJobsPerTech} layout="vertical" margin={{ left: 20, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="rgba(0,0,0,0.08)" />
              <XAxis type="number" tick={{ fontSize: 12, fill: '#605e5c' }} axisLine={false} tickLine={false} />
              <YAxis
                dataKey="name"
                type="category"
                tick={{ fontSize: 12, fill: '#323130' }}
                axisLine={false}
                tickLine={false}
                width={70}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e1dfdd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  fontSize: '12px',
                }}
                formatter={(value: number) => [`${value} jobs`, 'Open Jobs']}
              />
              <Bar dataKey="openJobs" radius={[0, 4, 4, 0]} maxBarSize={28}>
                {openJobsPerTech.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={TECH_COLORS[index % TECH_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Completed Jobs This Week" subtitle="Jobs resolved Mon-Fri by tech">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={completedJobsPerTech} margin={{ left: -10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.08)" />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#605e5c' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#605e5c' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e1dfdd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  fontSize: '12px',
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                iconType="circle"
                iconSize={8}
              />
              {techs.map((tech, index) => (
                <Bar
                  key={tech.id}
                  dataKey={tech.name.split(' ')[0]}
                  fill={TECH_COLORS[index]}
                  radius={[2, 2, 0, 0]}
                  maxBarSize={16}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="stats-view__charts-row">
        <ChartCard title="Jobs by Priority" subtitle="Distribution across priority levels">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={jobsByPriority} margin={{ left: -10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.08)" />
              <XAxis dataKey="priority" tick={{ fontSize: 12, fill: '#323130', fontWeight: 600 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#605e5c' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e1dfdd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  fontSize: '12px',
                }}
                formatter={(value: number) => [`${value} jobs`, 'Count']}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={60}>
                {jobsByPriority.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Jobs Over Time" subtitle="Created vs resolved (last 7 days)">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={jobsOverTime} margin={{ left: -10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.08)" />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#605e5c' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#605e5c' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e1dfdd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  fontSize: '12px',
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                iconType="circle"
                iconSize={8}
              />
              <Line
                type="monotone"
                dataKey="created"
                name="Created"
                stroke="#1976d2"
                strokeWidth={2}
                dot={{ fill: '#1976d2', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#1976d2' }}
              />
              <Line
                type="monotone"
                dataKey="resolved"
                name="Resolved"
                stroke="#107c10"
                strokeWidth={2}
                dot={{ fill: '#107c10', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#107c10' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
        </div>
      ) : (
        <AnalyticsExplorer />
      )}
    </div>
  );
};
